import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Activity,
  Shield,
  Workflow,
  Thermometer,
  Leaf,
  RefreshCw,
  Inbox,
  AlertTriangle,
  Radio,
  CheckCircle,
  Clock,
  Lightbulb,
  Info,
  Nfc,
  QrCode,
  Archive,
  Sparkles,
  TrendingDown,
  FileCheck,
  Snowflake,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import StatCard from '../components/dashboard/StatCard';
import DataSourceBadge from '../components/common/DataSourceBadge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [intelligence, setIntelligence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashRes, intelRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboard`),
        axios.get(`${BACKEND_URL}/api/sustainability-intelligence`),
      ]);
      setStats(dashRes.data);
      setIntelligence(intelRes.data);
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Error fetching dashboard:', e);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    toast.success('Refreshing dashboard...');
    await fetchDashboardData();
  };

  const getRelativeTime = (date) => {
    if (!date) return '';
    const s = Math.floor((new Date() - date) / 1000);
    if (s < 60) return 'Just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} minute${m > 1 ? 's' : ''} ago`;
    return `${Math.floor(m / 60)} hour${Math.floor(m / 60) > 1 ? 's' : ''} ago`;
  };

  const getRoleName = (role) => ({
    supply_chain_manager: 'Supply Chain Manager',
    sustainability_manager: 'Sustainability Manager',
    external_viewer: 'External Auditor',
  }[role] || role);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const hasBatchData = stats?.has_batch_data === true;
  const hasComplianceData = stats?.has_compliance_data === true;
  const liveActiveSessions = stats?.active_monitoring_sessions || 0;
  const liveCritical = stats?.total_critical_alarms || 0;
  const liveWarning = stats?.total_warning_alarms || 0;
  const liveReadings = stats?.total_sensor_readings || 0;
  const hasColdChainData = liveReadings > 0 || liveActiveSessions > 0;

  // ===== Operational readiness — computed from real data flags =====
  const readiness = [
    {
      key: 'traceability',
      label: 'Traceability',
      ready: hasBatchData,
      icon: Workflow,
      emptyText: 'Awaiting batch records',
    },
    {
      key: 'compliance',
      label: 'Compliance',
      ready: hasComplianceData,
      icon: Shield,
      emptyText: 'Awaiting compliance events',
    },
    {
      key: 'sustainability',
      label: 'Sustainability',
      ready: hasBatchData,
      icon: Leaf,
      emptyText: 'Awaiting sustainability records',
    },
    {
      key: 'cold-chain',
      label: 'Cold Chain',
      ready: hasColdChainData,
      icon: Thermometer,
      emptyText: 'Awaiting sensor activity',
    },
  ];

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <h1
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-1 sm:mb-2 break-words"
            style={{ fontFamily: 'Manrope' }}
          >
            Welcome back, {user.name}
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-slate-600">
            {getRoleName(user.role)} Dashboard
          </p>
          {lastUpdated && (
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Last updated: {getRelativeTime(lastUpdated)}
            </p>
          )}
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          variant="outline"
          data-testid="refresh-button"
          className="self-start sm:self-auto h-10"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Supply-Chain KPI Cards — 6 metrics, always visible */}
      <section data-testid="supply-chain-kpi-section">
        <div className="flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-2">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
            Supply Chain Overview
          </h2>
          {!hasBatchData && !hasColdChainData && (
            <DataSourceBadge source="empty" label="Pending Live Batch Data" />
          )}
        </div>

        <div
          className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6"
          data-testid="stats-grid"
        >
          <KpiCard
            icon={Package} label="Total Batches"
            ready={hasBatchData}
            value={stats?.total_batches}
            emptyText="No batch records yet"
            testId="kpi-total-batches"
          />
          <KpiCard
            icon={Activity} label="Active Batches"
            ready={hasBatchData}
            value={stats?.active_batches}
            trend="In transit or processing"
            emptyText="No active batches"
            testId="kpi-active-batches"
          />
          <KpiCard
            icon={Shield} label="Compliance Readiness"
            ready={hasComplianceData}
            value={hasComplianceData ? `${stats?.compliance_rate}%` : null}
            trend={hasComplianceData ? `Avg integrity: ${stats?.avg_integrity_score}` : null}
            emptyText="No compliance data"
            testId="kpi-compliance"
          />
          <KpiCard
            icon={Workflow} label="Traceability Events"
            ready={(stats?.traceability_events_total ?? 0) > 0}
            value={Number(stats?.traceability_events_total ?? 0).toLocaleString()}
            trend="Chain of Custody · NFC · Compliance · Temperature"
            emptyText="No traceability events recorded"
            testId="kpi-traceability-events"
          />
          <ColdChainStatusCard
            ready={hasColdChainData}
            value={
              hasColdChainData
                ? (liveCritical > 0 ? 'Critical' : liveWarning > 0 ? 'Warning' : 'Normal')
                : null
            }
            trend={hasColdChainData
              ? `${liveActiveSessions} active · ${liveReadings} readings`
              : null
            }
            severity={liveCritical > 0 ? 'critical' : liveWarning > 0 ? 'warning' : 'normal'}
            criticalCount={liveCritical}
            lastCritical={stats?.last_critical_alarm}
            onOpenColdChain={() => navigate('/mvp/sensor-console')}
          />
          <KpiCard
            icon={Leaf} label="Sustainability Status"
            ready={hasBatchData}
            value={hasBatchData ? Number(stats?.total_co2_kg || 0).toLocaleString() : null}
            trend={hasBatchData ? 'kg CO₂ recorded' : null}
            emptyText="No sustainability records"
            testId="kpi-sustainability"
          />
        </div>
      </section>

      {/* Operational Status — readiness indicators */}
      <section data-testid="operational-status-section">
        <div className="flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-2">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
            Operational Status
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {readiness.map(({ key, label, ready, icon: Icon, emptyText }) => (
            <div
              key={key}
              data-testid={`readiness-${key}`}
              className={`rounded-xl p-3 sm:p-4 border ${ready ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg flex-shrink-0 ${ready ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${ready ? 'text-emerald-700' : 'text-slate-500'}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-600">{label}</p>
                  {ready ? (
                    <p className="text-sm sm:text-base font-semibold text-emerald-700 mt-0.5 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Live
                    </p>
                  ) : (
                    <p className="text-xs sm:text-sm font-medium text-slate-500 mt-0.5 leading-snug">
                      {emptyText}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Chain of Custody — live executive summary */}
      <ChainOfCustodyCard
        stages={stats?.coc_stage_coverage || []}
        events={stats?.coc_recent_events || []}
        totalEvents={stats?.coc_total_events || 0}
        tracedBatches={stats?.coc_traced_batches || 0}
        mostAdvanced={stats?.coc_most_advanced_stage || null}
        onOpen={() => navigate('/mvp/chain-of-custody')}
      />

      {/* Recent Batches — operational heartbeat (positioned above CO₂ insights) */}
      <RecentBatchesCard
        rows={stats?.recent_batches_enriched || []}
        activeCount={stats?.batch_active_count || 0}
        completedCount={stats?.batch_completed_count || 0}
        archivedCount={stats?.batch_archived_count || 0}
        onOpen={(id) => navigate(`/mvp/batches/${id}`)}
        onOpenArchive={() => navigate('/mvp/batches?archived=true')}
      />

      {/* Sustainability Intelligence — capability preview + future analytics surface */}
      <SustainabilityIntelligenceCard
        hasBatchData={hasBatchData}
        hasComplianceData={hasComplianceData}
        hasColdChainData={hasColdChainData}
        cocTotalEvents={stats?.coc_total_events || 0}
        intelligence={intelligence}
      />
    </div>
  );
};

/* ----------------------- helpers ----------------------- */

const KpiCard = ({ icon: Icon, label, ready, value, trend, emptyText, valueColor, testId }) => {
  if (!ready || value === null || value === undefined) {
    return (
      <div
        className="bg-white rounded-xl p-3 sm:p-4 lg:p-6 border border-dashed border-slate-200"
        data-testid={`${testId}-empty`}
      >
        <div className="flex items-start gap-2 sm:gap-3 lg:gap-4">
          <div className="p-2 sm:p-2.5 lg:p-3 bg-slate-100 rounded-lg flex-shrink-0">
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-slate-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] sm:text-xs text-slate-600 mb-0.5 sm:mb-1 uppercase tracking-wider truncate">
              {label}
            </p>
            <p className="text-sm sm:text-base font-medium text-slate-500 leading-snug">
              {emptyText}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <StatCard
      icon={Icon}
      label={label}
      value={value}
      trend={trend}
      iconColor="text-slate-700"
      bgColor="bg-slate-100"
      data-testid={testId}
      // pass through value color (StatCard doesn't support it; override via simple wrap below)
    />
  );
};

/**
 * ColdChainStatusCard — Cold Chain KPI with integrated alert footer.
 *
 * Renders inline within Supply Chain Overview. When critical alarms exist,
 * the card itself becomes the alert surface (no separate banner). When no
 * alarms exist, behaves identically to the other KPI tiles.
 */
const ColdChainStatusCard = ({
  ready, value, trend, severity, criticalCount = 0, lastCritical, onOpenColdChain,
}) => {
  // Empty state — match KpiCard empty layout exactly
  if (!ready || value === null || value === undefined) {
    return (
      <div
        className="bg-white rounded-xl p-3 sm:p-4 lg:p-6 border border-dashed border-slate-200"
        data-testid="kpi-cold-chain-status-empty"
      >
        <div className="flex items-start gap-2 sm:gap-3 lg:gap-4">
          <div className="p-2 sm:p-2.5 lg:p-3 bg-slate-100 rounded-lg flex-shrink-0">
            <Thermometer className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-slate-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] sm:text-xs text-slate-600 mb-0.5 sm:mb-1 uppercase tracking-wider truncate">
              Cold Chain Status
            </p>
            <p className="text-sm sm:text-base font-medium text-slate-500 leading-snug">
              No sensor activity yet
            </p>
          </div>
        </div>
      </div>
    );
  }

  const badgeStyle =
    severity === 'critical' ? 'bg-rose-50 text-rose-700 border-rose-200'
      : severity === 'warning' ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  const showAlertFooter = severity === 'critical' && criticalCount > 0;

  const Wrapper = showAlertFooter ? 'button' : 'div';
  const wrapperProps = showAlertFooter
    ? { type: 'button', onClick: onOpenColdChain, 'aria-label': 'Open Cold Chain Monitoring' }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      data-testid="kpi-cold-chain-status"
      className={`text-left bg-white rounded-xl border border-slate-200 hover:shadow-lg transition-shadow duration-200 overflow-hidden w-full ${showAlertFooter ? 'hover:border-rose-300' : ''}`}
    >
      {/* Top — same anatomy as StatCard */}
      <div className="p-3 sm:p-4 lg:p-6">
        <div className="flex items-start gap-2 sm:gap-3 lg:gap-4">
          <div className="p-2 sm:p-2.5 lg:p-3 bg-slate-100 rounded-lg flex-shrink-0">
            <Thermometer className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-slate-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] sm:text-xs text-slate-600 mb-1 sm:mb-1.5 uppercase tracking-wider truncate">
              Cold Chain Status
            </p>
            <span
              className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full border text-xs sm:text-sm font-semibold ${badgeStyle}`}
            >
              {value}
            </span>
            {trend && (
              <p className="text-[11px] sm:text-xs lg:text-sm leading-snug text-slate-500 mt-1.5 sm:mt-2">
                {trend}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Integrated alert footer — only when critical */}
      {showAlertFooter && (
        <div
          data-testid="cold-chain-alert-footer"
          className="border-t border-rose-200 bg-rose-50 px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-rose-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] sm:text-xs font-semibold text-rose-900">
                {criticalCount} Critical Alert{criticalCount > 1 ? 's' : ''}
              </p>
              {lastCritical && (
                <p className="text-[11px] sm:text-xs text-rose-800 truncate">
                  Latest: <strong>{lastCritical.temperature_celsius?.toFixed(2)}°C</strong>
                  {lastCritical.product_name ? ` — ${lastCritical.product_name}` : ''}
                </p>
              )}
            </div>
            <span className="text-[11px] sm:text-xs font-medium text-rose-700 whitespace-nowrap flex-shrink-0">
              View Details →
            </span>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

const EmptyStateInline = ({ message }) => (
  <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 sm:p-8 text-center">
    <div className="inline-flex items-center justify-center w-10 h-10 bg-white border border-slate-200 rounded-full mb-3">
      <Inbox className="w-5 h-5 text-slate-400" />
    </div>
    <p className="text-sm sm:text-base text-slate-600">{message}</p>
  </div>
);

/* =====================================================================
 * RecentBatchesCard — Dashboard operational heartbeat.
 *
 * Renders:
 *   - 3 lifecycle KPI tiles: Active · Completed · Archived (always visible,
 *     show zeros in empty state).
 *   - List of up to 5 most recently updated batches across all lifecycle
 *     states, each row showing: batch_number, product_name, current stage
 *     label, status badge, relative "last updated" time, and capability
 *     icons (NFC / QR / Temperature) reflecting what's wired up for that batch.
 *   - Clean empty state: "Awaiting first registered product batch."
 *
 * Data source: /api/dashboard (recent_batches_enriched + batch_*_count).
 * No new endpoint, no schema change.
 * ===================================================================== */
const RecentBatchesCard = ({ rows, activeCount, completedCount, archivedCount, onOpen, onOpenArchive }) => {
  const hasAny = rows.length > 0;
  const total = activeCount + completedCount;

  return (
    <section
      data-testid="recent-batches-section"
      className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3 sm:mb-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
            <Package className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
          </div>
          <div className="min-w-0">
            <h2
              className="text-base sm:text-lg lg:text-xl font-semibold text-slate-800"
              style={{ fontFamily: 'Manrope' }}
            >
              Recent Batches
            </h2>
            <p className="text-[11px] sm:text-xs text-slate-600 mt-0.5">
              Operational summary of active batch activity across the canonical chain.
            </p>
          </div>
        </div>
        {hasAny ? <DataSourceBadge source="live" /> : <DataSourceBadge source="empty" label="Pending Live Batch Data" />}
      </div>

      {/* Lifecycle KPI tiles — Active & Completed only (Archived is intentionally
          hidden from the dashboard; see View archive link in the footer). */}
      <div
        className="grid grid-cols-2 gap-2 sm:gap-3 mb-4"
        data-testid="recent-batches-kpis"
      >
        <LifecycleKpi label="Active"    value={activeCount}    icon={Activity}    tone="emerald" testId="batches-kpi-active" />
        <LifecycleKpi label="Completed" value={completedCount} icon={CheckCircle} tone="sky"     testId="batches-kpi-completed" />
      </div>

      {/* List or empty state */}
      {hasAny ? (
        <ul className="divide-y divide-slate-100 -mx-4 sm:-mx-6 border-t border-slate-100">
          {rows.map((b) => (
            <li
              key={b.id}
              data-testid={`recent-batch-${b.id}`}
              onClick={() => onOpen?.(b.id)}
              className="p-3 sm:p-4 sm:px-6 hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-wrap sm:flex-nowrap">
                <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] sm:text-xs text-slate-500 truncate">{b.batch_number}</span>
                    <LifecycleBadge lifecycle={b.lifecycle} />
                  </div>
                  <p className="text-sm sm:text-base font-semibold text-slate-800 truncate">{b.product_name}</p>
                  <div className="flex items-center gap-2 flex-wrap mt-1 text-[11px] sm:text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                      <Workflow className="w-3 h-3" aria-hidden="true" />
                      {b.current_stage_label}
                    </span>
                    <span className="text-slate-400">·</span>
                    <span className="inline-flex items-center gap-1 text-slate-500">
                      <Clock className="w-3 h-3" aria-hidden="true" />
                      Updated {relativeTime(b.last_updated)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <CapabilityIcon active={b.has_qr} title="QR verification ready" icon={QrCode} />
                  <CapabilityIcon
                    active={b.has_nfc}
                    title={b.has_nfc ? `NFC tag bound · ${b.nfc_scan_count || 0} scan${b.nfc_scan_count === 1 ? '' : 's'}` : 'NFC tag not bound'}
                    icon={Nfc}
                  />
                  <CapabilityIcon
                    active={b.has_temperature}
                    title={b.has_temperature ? 'Temperature data attached' : 'No temperature data yet'}
                    icon={Thermometer}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div
          className="bg-slate-50/60 border border-dashed border-slate-200 rounded-lg p-6 text-center"
          data-testid="recent-batches-empty-state"
        >
          <Inbox className="w-8 h-8 text-slate-400 mx-auto mb-2" aria-hidden="true" />
          <p className="text-sm sm:text-base font-semibold text-slate-700">
            Awaiting first registered product batch.
          </p>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Batches you register will appear here in real time.
          </p>
        </div>
      )}

      {/* Footer — pagination hint + archive link */}
      <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        {hasAny && total > rows.length ? (
          <p className="text-[11px] sm:text-xs text-slate-500">
            Showing the {rows.length} most recent of {total} active batches.
          </p>
        ) : <span />}
        {archivedCount > 0 && onOpenArchive && (
          <button
            type="button"
            onClick={onOpenArchive}
            data-testid="recent-batches-view-archive"
            className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors self-end sm:self-auto"
          >
            <Archive className="w-3.5 h-3.5" aria-hidden="true" />
            View archive ({archivedCount})
          </button>
        )}
      </div>
    </section>
  );
};

const LifecycleKpi = ({ label, value, icon: Icon, tone, testId }) => {
  const toneClasses = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    sky:     'bg-sky-50 border-sky-200 text-sky-800',
    slate:   'bg-slate-50 border-slate-200 text-slate-700',
  }[tone] || 'bg-slate-50 border-slate-200 text-slate-700';
  const valueActive = value > 0;
  return (
    <div
      data-testid={testId}
      className={`rounded-lg border px-3 py-2.5 sm:py-3 ${toneClasses} ${valueActive ? '' : 'opacity-60'}`}
    >
      <div className="flex items-center gap-1.5 text-[9px] sm:text-[10px] uppercase tracking-wider font-semibold">
        <Icon className="w-3 h-3" aria-hidden="true" />
        {label}
      </div>
      <div
        className="text-xl sm:text-2xl font-bold mt-0.5 text-slate-900"
        style={{ fontFamily: 'Manrope' }}
      >
        {value}
      </div>
    </div>
  );
};

const LifecycleBadge = ({ lifecycle }) => {
  const cfg = {
    active:    { label: 'Active',    cls: 'bg-emerald-50 border-emerald-200 text-emerald-800', dot: 'bg-emerald-500' },
    completed: { label: 'Completed', cls: 'bg-sky-50 border-sky-200 text-sky-800',             dot: 'bg-sky-500' },
    archived:  { label: 'Archived',  cls: 'bg-slate-100 border-slate-200 text-slate-600',      dot: 'bg-slate-400' },
    alert:     { label: 'Alert',     cls: 'bg-rose-50 border-rose-200 text-rose-700',          dot: 'bg-rose-500' },
  }[lifecycle] || { label: lifecycle || 'Unknown', cls: 'bg-slate-100 border-slate-200 text-slate-700', dot: 'bg-slate-400' };
  return (
    <span
      data-testid={`batch-lifecycle-${lifecycle}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold border ${cfg.cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const CapabilityIcon = ({ icon: Icon, active, title }) => (
  <span
    title={title}
    aria-label={title}
    className={`inline-flex items-center justify-center w-7 h-7 rounded-md border ${
      active
        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
        : 'bg-slate-50 border-slate-200 text-slate-300'
    }`}
  >
    <Icon className="w-3.5 h-3.5" aria-hidden="true" />
  </span>
);

/* =====================================================================
 * ChainOfCustodyCard — Dashboard executive summary.
 *
 * Always renders the KPI strip (stage coverage / traced batches / movements /
 * most advanced stage), even in empty state — so the card feels like an
 * always-on cockpit, not a placeholder. Stage pill row + latest movements
 * feed appear once any chain-of-custody event has been recorded.
 *
 * Data source: /api/dashboard (coc_stage_coverage, coc_recent_events,
 * coc_total_events, coc_traced_batches, coc_most_advanced_stage). No new
 * endpoint, no parallel chain-of-custody system. Archived batches excluded.
 * ===================================================================== */
const ChainOfCustodyCard = ({ stages, events, totalEvents, tracedBatches, mostAdvanced, onOpen }) => {
  // Normalise to always 11 stages even if backend returns []
  const stageList = stages.length === 11 ? stages : Array.from({ length: 11 }).map((_, i) => ({
    stage_key: `stage_${i}`,
    label: '—',
    reached: false,
  }));
  const reachedCount = stageList.filter((s) => s.reached).length;
  const hasAny = totalEvents > 0;

  return (
    <section
      data-testid="dashboard-coc-teaser"
      className="bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-200 p-4 sm:p-6"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
            <Workflow className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-700" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2
                className="text-base sm:text-lg lg:text-xl font-semibold text-slate-900"
                style={{ fontFamily: 'Manrope' }}
              >
                11-Stage Chain of Custody
              </h2>
              <span
                data-testid="dashboard-coc-canonical-badge"
                title="PureSustain standardizes seafood traceability through an 11-stage chain-of-custody framework adaptable to operators, facilities, and supply chains."
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-300/80 text-[10px] sm:text-[11px] font-semibold text-emerald-800 uppercase tracking-wider cursor-help"
              >
                <Info className="w-3 h-3" aria-hidden="true" />
                11-Stage Canonical Model
              </span>
            </div>
            <p
              className="text-[11px] sm:text-xs text-slate-600 mt-1"
              data-testid="dashboard-coc-subtitle"
            >
              Executive summary of traceability activity across active batches.
            </p>
          </div>
        </div>
        <Button
          onClick={onOpen}
          data-testid="dashboard-coc-open-button"
          className="bg-emerald-700 hover:bg-emerald-800 text-white h-10 flex-shrink-0"
        >
          Open Chain of Custody
        </Button>
      </div>

      {/* KPI strip — always rendered, shows zeros in empty state */}
      <div
        className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mt-4"
        data-testid="dashboard-coc-kpis"
      >
        <KpiTile
          label="Stage Coverage"
          value={`${reachedCount}/11`}
          sub="stages reached"
          testId="coc-kpi-coverage"
          active={hasAny}
        />
        <KpiTile
          label="Active Batches"
          value={String(tracedBatches)}
          sub={tracedBatches === 1 ? 'traced batch' : 'traced batches'}
          testId="coc-kpi-traced-batches"
          active={tracedBatches > 0}
        />
        <KpiTile
          label="Recent Movements"
          value={String(events.length)}
          sub={events.length === 1 ? 'movement' : 'movements'}
          testId="coc-kpi-recent-movements"
          active={events.length > 0}
        />
        <KpiTile
          label="Most Advanced Stage"
          value={mostAdvanced?.label || '—'}
          sub={mostAdvanced ? 'farthest reached' : 'Awaiting first traceability event'}
          testId="coc-kpi-most-advanced"
          active={!!mostAdvanced}
          mono={false}
        />
      </div>

      {/* Stage coverage pills */}
      <div
        className="flex flex-wrap gap-1.5 mt-4"
        data-testid="dashboard-coc-stage-pills"
      >
        {stageList.map((s) => {
          const reached = !!s.reached;
          return (
            <span
              key={s.stage_key}
              data-testid={`coc-stage-pill-${s.stage_key}`}
              data-reached={reached ? 'true' : 'false'}
              title={reached ? `${s.label} — reached` : `${s.label} — pending`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium border transition-colors ${
                reached
                  ? 'bg-emerald-600 border-emerald-700 text-white'
                  : 'bg-white border-emerald-200/70 text-slate-500'
              }`}
            >
              {reached && <CheckCircle className="w-3 h-3" aria-hidden="true" />}
              {s.label}
            </span>
          );
        })}
      </div>

      {/* Latest movements feed (or empty state) */}
      {hasAny ? (
        events.length > 0 && (
          <div
            className="mt-4 pt-4 border-t border-emerald-100"
            data-testid="dashboard-coc-recent-events"
          >
            <p className="text-[10px] sm:text-xs uppercase tracking-wider font-semibold text-emerald-800 mb-2">
              Latest movements
            </p>
            <ul className="space-y-1.5">
              {events.map((e, i) => (
                <li
                  key={`${e.batch_id}-${i}`}
                  data-testid={`coc-recent-event-${i}`}
                  className="flex items-center gap-2 text-xs sm:text-sm text-slate-700"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                  <span className="font-semibold text-slate-800 truncate">{e.product_name}</span>
                  <span className="text-slate-400">→</span>
                  <span className="text-emerald-800 font-medium">{e.stage_label}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-500 whitespace-nowrap">{relativeTime(e.timestamp)}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      ) : (
        <div
          className="mt-4 pt-4 border-t border-emerald-100 text-center"
          data-testid="dashboard-coc-empty-state"
        >
          <p className="text-xs sm:text-sm text-slate-600">
            No chain-of-custody events recorded yet.
          </p>
          <p className="text-[11px] sm:text-xs text-slate-500 mt-1">
            Movements logged on the Chain of Custody page will appear here in real time.
          </p>
        </div>
      )}
    </section>
  );
};

const KpiTile = ({ label, value, sub, testId, active, mono = false }) => (
  <div
    data-testid={testId}
    className={`rounded-lg border px-3 py-2.5 sm:py-3 transition-colors ${
      active
        ? 'bg-white border-emerald-300/80 shadow-sm'
        : 'bg-white/60 border-emerald-200/70'
    }`}
  >
    <div className="text-[9px] sm:text-[10px] uppercase tracking-wider font-semibold text-emerald-800/80">
      {label}
    </div>
    <div
      className={`text-base sm:text-lg lg:text-xl font-bold mt-0.5 truncate ${
        active ? 'text-slate-900' : 'text-slate-400'
      } ${mono ? 'font-mono' : ''}`}
      style={{ fontFamily: 'Manrope' }}
    >
      {value}
    </div>
    <div className="text-[10px] sm:text-[11px] text-slate-500 truncate">{sub}</div>
  </div>
);

const relativeTime = (iso) => {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    if (isNaN(t)) return '—';
    const diff = Date.now() - t;
    if (diff < 60_000)      return 'Just now';
    if (diff < 3_600_000)   return `${Math.floor(diff / 60_000)} min ago`;
    if (diff < 86_400_000)  return `${Math.floor(diff / 3_600_000)} h ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return '—'; }
};

/* =====================================================================
 * SustainabilityIntelligenceCard — Dashboard analytics surface.
 *
 * Communicates that PureSustain converts traceability, cold-chain, and
 * sustainability signals into actionable insights. Renders an "always-on"
 * capability preview (6 insight tiles) whose readiness pills flip from
 * "Pending data" → "Ready" as the underlying data sources start flowing.
 *
 * UI-only: no calculations are run yet. When the analytics engine ships,
 * each tile becomes a live metric without changing the surrounding
 * presentation.
 * ===================================================================== */
const SustainabilityIntelligenceCard = ({
  hasBatchData,
  hasComplianceData,
  hasColdChainData,
  cocTotalEvents,
  intelligence,
}) => {
  const hasTraceabilityData = cocTotalEvents > 0;
  const cc = intelligence?.cold_chain || {};
  const tr = intelligence?.traceability || {};
  const su = intelligence?.sustainability || {};

  // ---- Formatters (return "—" for null/undefined to keep tiles honest) ----
  const fmtPct = (v) =>
    v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`;
  const fmtInt = (v) =>
    v === null || v === undefined ? '—' : Number(v).toLocaleString();
  const fmtTemp = (v) =>
    v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}°C`;
  const fmtScore = (v) =>
    v === null || v === undefined ? '—' : Number(v).toFixed(1);
  const fmtCo2 = (v) =>
    v === null || v === undefined ? '—' : `${Number(v).toLocaleString()} kg`;

  // A tile is "ready" when its composite score is computable.
  const ccReady = cc.integrity_score !== null && cc.integrity_score !== undefined;
  const trReady = tr.readiness_score !== null && tr.readiness_score !== undefined;
  const suReady = su.sustainability_score !== null && su.sustainability_score !== undefined;

  // Three intelligence domains — each with up to 5 metric rows + a
  // composite score. Empty values render as "—" and the tile stays in
  // "Pending data" state. We never imply performance without evidence.
  const insights = [
    {
      key: 'cold-chain',
      icon: Snowflake,
      title: 'Cold-Chain Intelligence',
      desc: 'Temperature compliance, excursion activity, and cold-chain integrity.',
      ready: ccReady,
      band: 'capture',
      metrics: [
        { label: 'Temperature Compliance', value: fmtPct(cc.temp_compliance_pct) },
        { label: 'Critical Alerts', value: fmtInt(cc.critical_alerts) },
        { label: 'Warning Alerts', value: fmtInt(cc.warning_alerts) },
        { label: 'Average Batch Temp.', value: fmtTemp(cc.avg_temp_c) },
      ],
      score: { label: 'Cold-Chain Integrity Score', value: fmtScore(cc.integrity_score), max: 100 },
      empty: 'Awaiting telemetry',
    },
    {
      key: 'traceability',
      icon: Workflow,
      title: 'Traceability Intelligence',
      desc: 'Chain-of-custody coverage, verification reach, and audit footprint.',
      ready: trReady,
      band: 'verify',
      metrics: [
        { label: 'Traceability Completeness', value: fmtPct(tr.completeness_pct) },
        { label: 'Checkpoints Completed', value: fmtInt(tr.checkpoints_completed) },
        { label: 'NFC / QR Verification', value: fmtPct(tr.nfc_coverage_pct) },
        { label: 'Audit Event Coverage', value: fmtInt(tr.audit_event_coverage) },
        { label: 'Audit Integrity', value: fmtPct(tr.audit_integrity_pct) },
      ],
      score: { label: 'Verification Readiness Score', value: fmtScore(tr.readiness_score), max: 100 },
      empty: 'Awaiting traceability events',
    },
    {
      key: 'sustainability',
      icon: Leaf,
      title: 'Sustainability & Reporting',
      desc: 'Operational sustainability indicators and reporting readiness.',
      ready: suReady,
      band: 'report',
      metrics: [
        { label: 'Estimated CO₂ Impact', value: fmtCo2(su.co2_kg) },
        { label: 'Reporting Readiness', value: fmtPct(su.reporting_readiness_pct) },
        { label: 'Compliance Evidence', value: fmtInt(su.compliance_evidence_coverage) },
        { label: 'Data Completeness', value: fmtPct(su.data_completeness_pct) },
      ],
      score: { label: 'Sustainability Score', value: fmtScore(su.sustainability_score), max: 100 },
      empty: 'Awaiting sustainability records',
    },
  ];

  const readyCount = insights.filter((i) => i.ready).length;

  // Workflow chips — Capture → Verify → Report.
  // Capture: temperature telemetry, NFC scans, QR scans, batch events.
  // Verify:  chain of custody, compliance evidence, validation checkpoints.
  // Report:  sustainability outputs, audit readiness, customer transparency.
  const bands = [
    { key: 'capture', label: 'Capture', active: hasColdChainData || hasBatchData },
    { key: 'verify',  label: 'Verify',  active: hasTraceabilityData },
    { key: 'report',  label: 'Report',  active: hasComplianceData && hasTraceabilityData },
  ];

  // Status pill: communicate readiness, never inactivity.
  const statusReady = readyCount > 0;
  const statusLabel = statusReady ? 'Analytics Ready' : 'Awaiting Operational Data';

  return (
    <section
      data-testid="sustainability-intelligence-section"
      className="bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-200 p-4 sm:p-6"
    >
      {/* Header — mirrors Chain of Custody anatomy */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-700" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2
                className="text-base sm:text-lg lg:text-xl font-semibold text-slate-900"
                style={{ fontFamily: 'Manrope' }}
                data-testid="sustainability-intelligence-title"
              >
                Sustainability Intelligence
              </h2>
              <span
                title="PureSustain converts traceability, chain-of-custody, cold-chain, and operational records into actionable sustainability insights."
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-300/80 text-[10px] sm:text-[11px] font-semibold text-emerald-800 uppercase tracking-wider cursor-help"
              >
                <Info className="w-3 h-3" aria-hidden="true" />
                Insights Layer
              </span>
            </div>
            <p className="text-[11px] sm:text-xs lg:text-sm text-slate-600 mt-1 max-w-2xl">
              Operational sustainability indicators derived from existing
              cold-chain, traceability, and compliance records. Estimated CO₂
              is an operational metric — full lifecycle accounting arrives in a
              future release.
            </p>
          </div>
        </div>
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] sm:text-xs font-semibold tabular-nums ${
            statusReady
              ? 'bg-white border-emerald-300 text-emerald-800'
              : 'bg-white border-emerald-200 text-emerald-800/80'
          }`}
          data-testid="sustainability-intelligence-readiness"
          data-ready={statusReady ? 'true' : 'false'}
          data-ready-count={readyCount}
          data-signals-total={6}
          title="Future-ready for N/6 Signals Live across Traceability, NFC, QR, Temperature, Compliance, and Sustainability."
        >
          <CheckCircle className="w-3.5 h-3.5" aria-hidden="true" />
          {statusLabel}
        </div>
      </div>

      {/* Maturity strip — Operational → Traceability → Sustainability */}
      <div
        className="mt-4 flex items-center gap-1.5 sm:gap-2 flex-wrap"
        data-testid="sustainability-intelligence-maturity-strip"
        aria-label="Intelligence maturity progression"
      >
        {bands.map((b, i) => (
          <React.Fragment key={b.key}>
            <span
              data-testid={`maturity-band-${b.key}`}
              data-active={b.active ? 'true' : 'false'}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] sm:text-[11px] font-medium transition-colors ${
                b.active
                  ? 'bg-emerald-600 border-emerald-700 text-white'
                  : 'bg-white border-emerald-200/70 text-slate-500'
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  b.active ? 'bg-white' : 'bg-slate-300'
                }`}
                aria-hidden="true"
              />
              {b.label}
            </span>
            {i < bands.length - 1 && (
              <span className="text-emerald-700/60 text-xs" aria-hidden="true">→</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Capability preview grid — three intelligence domains */}
      <div
        className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mt-4"
        data-testid="sustainability-intelligence-grid"
      >
        {insights.map((it) => (
          <InsightCapabilityTile key={it.key} item={it} />
        ))}
      </div>

      {/* Footer note */}
      <div
        className="mt-4 pt-4 border-t border-emerald-100 flex items-start gap-2"
        data-testid="sustainability-intelligence-footer"
      >
        <Lightbulb className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-[11px] sm:text-xs lg:text-sm text-slate-600 leading-snug">
          Sustainability Intelligence activates automatically as operational, traceability,
          and cold-chain data accumulate across the supply chain.
        </p>
      </div>
    </section>
  );
};

const InsightCapabilityTile = ({ item }) => {
  const Icon = item.icon;
  return (
    <div
      data-testid={`insight-tile-${item.key}`}
      data-ready={item.ready ? 'true' : 'false'}
      className={`relative rounded-lg border p-4 transition-colors flex flex-col ${
        item.ready
          ? 'bg-white border-emerald-300/80 shadow-sm'
          : 'bg-white/70 border-emerald-200/70'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`p-2 rounded-md flex-shrink-0 ${
            item.ready ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
          }`}
        >
          <Icon className="w-5 h-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p
              className={`text-sm sm:text-[15px] font-semibold leading-snug ${
                item.ready ? 'text-slate-900' : 'text-slate-700'
              }`}
              style={{ fontFamily: 'Manrope' }}
            >
              {item.title}
            </p>
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold border whitespace-nowrap ${
                item.ready
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                  : 'bg-slate-50 border-slate-200 text-slate-500'
              }`}
              data-testid={`insight-tile-${item.key}-readiness`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  item.ready ? 'bg-emerald-500' : 'bg-slate-400'
                }`}
              />
              {item.ready ? 'Ready' : 'Pending data'}
            </span>
          </div>
          {item.desc && (
            <p className="mt-1 text-[11px] sm:text-xs text-slate-500 leading-snug">
              {item.desc}
            </p>
          )}
        </div>
      </div>

      {/* Composite score — focal point of the tile. Renders "—" when score
          cannot be computed (insufficient data). */}
      {item.score && (
        <div
          data-testid={`insight-tile-${item.key}-score`}
          className="mt-4 flex items-baseline gap-2"
        >
          <span
            className={`text-2xl sm:text-3xl font-bold tabular-nums ${
              item.ready ? 'text-emerald-700' : 'text-slate-400'
            }`}
            style={{ fontFamily: 'Manrope' }}
          >
            {item.score.value}
          </span>
          {item.score.max && item.ready && (
            <span className="text-xs text-slate-400 font-medium">
              / {item.score.max}
            </span>
          )}
          <span className="text-[10px] sm:text-[11px] text-slate-500 ml-1 leading-tight">
            {item.score.label}
          </span>
        </div>
      )}

      {/* Metric rows — each metric formatted with unit. "—" when no data. */}
      {item.metrics && item.metrics.length > 0 && (
        <div
          data-testid={`insight-tile-${item.key}-metrics`}
          className="mt-3 pt-3 border-t border-slate-100 space-y-1.5"
        >
          {item.metrics.map((m) => (
            <div
              key={m.label}
              className="flex items-center justify-between gap-2 text-[11px] sm:text-xs"
              data-testid={`insight-tile-${item.key}-metric-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
            >
              <span className="text-slate-500 truncate">{m.label}</span>
              <span
                className={`font-semibold tabular-nums whitespace-nowrap ${
                  m.value === '—' ? 'text-slate-300' : 'text-slate-800'
                }`}
              >
                {m.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {!item.ready && !item.metrics && (
        <div className="mt-4">
          <p className="text-[11px] sm:text-xs leading-snug text-slate-400" style={{ fontFamily: 'Manrope' }}>
            {item.empty}
          </p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
